import { motion } from 'framer-motion'
import { Sparkles, ArrowRight } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

interface WelcomeBannerProps {
    userName: string
    progress: number // 0-100
    onContinue?: () => void
}

export function WelcomeBanner({ userName, progress, onContinue }: WelcomeBannerProps) {
    const getGreeting = () => {
        const hour = new Date().getHours()
        if (hour < 12) return 'Good Morning'
        if (hour < 17) return 'Good Afternoon'
        return 'Good Evening'
    }

    const circumference = 2 * Math.PI * 40 // radius = 40
    const strokeDashoffset = circumference - (progress / 100) * circumference

    return (
        <GlassmorphicCard className="p-4 md:p-6 relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute -top-20 -right-20 w-40 h-40 md:w-60 md:h-60 rounded-full bg-gradient-to-br from-primary-blue/30 to-accent-gold/20 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 md:w-48 md:h-48 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/10 blur-2xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
                {/* Left Section: Greeting & Message */}
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-accent-gold" />
                        <span className="text-xs md:text-sm font-medium text-accent-gold">{getGreeting()}</span>
                    </div>
                    <h1 className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-theme-primary mb-1 md:mb-2">
                        Welcome back, <span className="bg-gradient-to-r from-primary-blue to-accent-gold bg-clip-text text-transparent">{userName}</span>!
                    </h1>
                    <p className="text-sm md:text-base text-theme-secondary mb-4 md:mb-0">
                        You're making great progress. Keep up the momentum!
                    </p>

                    {/* Mobile CTA */}
                    <button
                        onClick={onContinue}
                        className="md:hidden mt-2 px-4 py-2 bg-gradient-to-r from-primary-blue to-blue-600 text-white text-sm font-semibold rounded-lg shadow-lg shadow-blue-500/25 flex items-center gap-2 hover:shadow-blue-500/40 transition-shadow"
                    >
                        Continue Learning
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>

                {/* Right Section: Progress Ring */}
                <div className="flex items-center gap-4 md:gap-6">
                    <div className="relative">
                        <svg className="w-20 h-20 md:w-24 md:h-24 -rotate-90">
                            {/* Background Circle */}
                            <circle
                                cx="50%"
                                cy="50%"
                                r="40"
                                stroke="currentColor"
                                strokeWidth="6"
                                fill="none"
                                className="text-slate-200 dark:text-slate-700"
                            />
                            {/* Progress Circle */}
                            <motion.circle
                                cx="50%"
                                cy="50%"
                                r="40"
                                stroke="url(#progressGradient)"
                                strokeWidth="6"
                                fill="none"
                                strokeLinecap="round"
                                initial={{ strokeDashoffset: circumference }}
                                animate={{ strokeDashoffset }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                                style={{ strokeDasharray: circumference }}
                            />
                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#4A90E2" />
                                    <stop offset="100%" stopColor="#D4A853" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="text-lg md:text-xl font-bold text-theme-primary"
                            >
                                {progress}%
                            </motion.span>
                            <span className="text-[10px] md:text-xs text-theme-secondary">Complete</span>
                        </div>
                    </div>

                    {/* Desktop CTA */}
                    <button
                        onClick={onContinue}
                        className="hidden md:flex px-5 py-3 bg-gradient-to-r from-primary-blue to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 items-center gap-2 hover:shadow-blue-500/40 transition-shadow"
                    >
                        Continue Learning
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </GlassmorphicCard>
    )
}
